import React, { useEffect, useState, useCallback } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import Shell from "@/components/Shell";
import { api, getToken } from "@/lib/api";

/**
 * /manual — operator-facing documentation surface.
 *
 * Pulls the bundle manifest from `/api/docs/manifest`, renders a sidebar
 * of files, fetches each one on click as plain markdown via
 * `/api/docs/file/{name}`. Plus a Download Bundle button that streams
 * the full ZIP via `/api/docs/bundle.zip`.
 *
 * Rendered as monospace pre-text — no markdown library dep added. Good
 * enough for an internal docs viewer; the operator's editing target is
 * Claude / their handler, not in-browser.
 *
 * Per No. 6 (22 May 2026): "is there an easy way for me to receive all
 * the system documents from the beginning in how things piece together,
 * I'm thinking a user manual as well as system docs". This is the
 * answer — zero LLM cost, deterministic content.
 */
const FILE_DESCRIPTIONS = {
    "USER_MANUAL.md":            "Start here. How to operate every surface.",
    "SYSTEM_OVERVIEW.md":        "Architecture diagram + every API endpoint, auto-generated.",
    "PRD.md":                    "Product canon. Decisions log. Original requirements.",
    "CHANGELOG.md":              "What changed, when, why. Append-only.",
    "ROADMAP.md":                "P0/P1/P2 backlog.",
    "three-origin.md":           "Three's brief — the founding instruction.",
    "test_credentials.md":       "Test accounts (internal).",
    "redeploy_briefing_20may.md": "Notes from the 20 May redeploy.",
};

function describeFile(name) {
    return FILE_DESCRIPTIONS[name] || (
        name.startsWith("SESSION_") ? "Session handoff between agents." :
        name.startsWith("COMMS_") ? "Inter-agent communications log." :
        "Memory doc."
    );
}

export default function Manual() {
    const [manifest, setManifest] = useState(null);
    const [audience, setAudience] = useState("engineer");
    const [active, setActive] = useState("USER_MANUAL.md");
    const [body, setBody] = useState("");
    const [bodyLoading, setBodyLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [err, setErr] = useState("");

    // Reload manifest when audience changes so the sidebar reflects which
    // files are about to ship. If the previously-active file is no longer
    // in the audience's list, fall back to USER_MANUAL.md.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/docs/manifest", {
                    params: { audience },
                });
                setManifest(data);
                const names = (data.files || []).map((f) => f.name);
                if (!names.includes(active)) {
                    setActive("USER_MANUAL.md");
                }
            } catch (e) {
                setErr(`Manifest failed: ${e?.response?.data?.detail || e.message}`);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audience]);

    const loadFile = useCallback(async (name) => {
        setActive(name);
        setBodyLoading(true);
        setBody("");
        try {
            const { data } = await api.get(`/docs/file/${encodeURIComponent(name)}`, {
                responseType: "text",
                transformResponse: [(d) => d], // bypass JSON parsing
            });
            setBody(typeof data === "string" ? data : JSON.stringify(data, null, 2));
        } catch (e) {
            setBody(`# Could not load ${name}\n\n${e?.response?.data?.detail || e.message}`);
        } finally {
            setBodyLoading(false);
        }
    }, []);

    useEffect(() => {
        loadFile(active);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const downloadBundle = useCallback(async () => {
        setDownloading(true);
        try {
            // Use fetch directly so we can stream the blob with the right
            // Authorization header. axios responseType: 'blob' also works
            // but this is cleaner for the one-shot download.
            const resp = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/docs/bundle.zip?audience=${encodeURIComponent(audience)}`,
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const today = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `spyfinance-docs-${audience}-${today}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            setErr(`Download failed: ${e.message}`);
        } finally {
            setDownloading(false);
        }
    }, [audience]);

    return (
        <Shell>
            <div className="max-w-7xl mx-auto px-4 py-6 space-y-6" data-testid="manual-page">
                {/* Header */}
                <div className="paper-card p-6">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                        <div>
                            <div className="label-typed text-stamp-red">Documentation</div>
                            <h1 className="font-heading text-3xl md:text-4xl text-ink mt-1">
                                Operator's Manual & System Docs
                            </h1>
                            <p className="font-body text-sm text-ink-soft mt-2 max-w-2xl">
                                Everything you need to drive the rig — plus the architecture under it.
                                Auto-bundled. Pull a fresh copy anytime; you'll get whatever's changed.
                            </p>
                        </div>
                        <div className="flex flex-col items-stretch md:items-end gap-3">
                            {/* Audience toggle — handler edition strips internal session logs
                                + test credentials so the ZIP is safe to share with Claude /
                                external observers. Engineer edition is the full archive. */}
                            <div className="inline-flex border border-ink/30" role="radiogroup" aria-label="Bundle audience" data-testid="manual-audience-toggle">
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={audience === "handler"}
                                    onClick={() => setAudience("handler")}
                                    data-testid="manual-audience-handler"
                                    className={`px-3 py-1.5 font-heading uppercase tracking-[0.18em] text-[10px] transition-colors ${
                                        audience === "handler"
                                            ? "bg-stamp-red text-paper-light"
                                            : "text-ink-soft hover:text-ink"
                                    }`}
                                    title="For Claude / external observers. Strips internal session logs + credentials."
                                >
                                    Handler
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={audience === "engineer"}
                                    onClick={() => setAudience("engineer")}
                                    data-testid="manual-audience-engineer"
                                    className={`px-3 py-1.5 font-heading uppercase tracking-[0.18em] text-[10px] border-l border-ink/30 transition-colors ${
                                        audience === "engineer"
                                            ? "bg-stamp-red text-paper-light"
                                            : "text-ink-soft hover:text-ink"
                                    }`}
                                    title="Full archive. Includes inter-agent comms + test credentials. Internal only."
                                >
                                    Engineer
                                </button>
                            </div>
                            <button
                                onClick={downloadBundle}
                                disabled={downloading}
                                data-testid="manual-download-zip"
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-stamp-red text-paper-light font-heading uppercase tracking-[0.22em] text-xs hover:bg-stamp-red/90 disabled:opacity-50 transition-colors"
                            >
                                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                {downloading ? "Bundling…" : `Download · ${audience}`}
                            </button>
                        </div>
                    </div>
                    {err && (
                        <div className="mt-4 px-3 py-2 border border-stamp-red/40 bg-stamp-red/5 text-stamp-red text-sm font-body" data-testid="manual-error">
                            {err}
                        </div>
                    )}
                </div>

                {/* Sidebar + viewer */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <aside className="paper-card p-3 md:col-span-1 md:max-h-[75vh] md:overflow-y-auto" data-testid="manual-sidebar">
                        <div className="label-typed text-[10px] tracking-[0.25em] text-stamp-red mb-2 px-2">
                            {manifest?.total_files ?? "…"} files
                        </div>
                        <ul className="space-y-1">
                            {(manifest?.files || []).map((f) => {
                                const isActive = f.name === active;
                                return (
                                    <li key={f.name}>
                                        <button
                                            onClick={() => loadFile(f.name)}
                                            data-testid={`manual-file-${f.name}`}
                                            className={`w-full text-left px-2 py-2 border-l-2 transition-colors ${
                                                isActive
                                                    ? "border-stamp-red bg-stamp-red/5"
                                                    : "border-transparent hover:border-ink/30"
                                            }`}
                                        >
                                            <div className={`flex items-center gap-1.5 font-heading text-[11px] tracking-[0.12em] ${isActive ? "text-stamp-red" : "text-ink"}`}>
                                                <FileText className="w-3 h-3" strokeWidth={1.6} />
                                                <span className="truncate">{f.name.replace(/\.md$/, "")}</span>
                                            </div>
                                            <div className="font-body text-[10px] text-ink-soft mt-0.5 pl-4 leading-snug">
                                                {describeFile(f.name)}
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </aside>

                    <section className="paper-card p-5 md:col-span-3 md:max-h-[75vh] md:overflow-y-auto" data-testid="manual-viewer">
                        <div className="label-typed text-[10px] tracking-[0.25em] text-stamp-red mb-3">
                            {active}
                        </div>
                        {bodyLoading ? (
                            <div className="flex items-center gap-2 text-ink-soft py-8">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="font-body text-sm">Loading…</span>
                            </div>
                        ) : (
                            <pre className="font-mono text-[12px] leading-relaxed text-ink whitespace-pre-wrap break-words" data-testid="manual-viewer-body">
                                {body}
                            </pre>
                        )}
                    </section>
                </div>
            </div>
        </Shell>
    );
}
